import React, { useState, useEffect, useRef } from 'react';
import { Spin, Alert, notification } from 'antd';
import ConfigService from '../services/ConfigService';
import { syncApiBaseUrl } from '../services/api';

/**
 * AppInitializer - Ensures ConfigService is initialized before app starts
 *
 * Cache-first strategy: when a cached config exists in localStorage the app
 * renders immediately and the config is revalidated in the background
 * (notifying the user if the backend URL changed). The blocking
 * "Loading Configuration..." screen only appears on the very first visit,
 * when no cached config exists yet.
 */
const AppInitializer = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [initError, setInitError] = useState(null);
    const didInit = useRef(false);

    useEffect(() => {
        // Guard against StrictMode double-invocation in development
        if (didInit.current) return;
        didInit.current = true;

        const revalidateInBackground = async () => {
            const previousUrl = ConfigService.getBackendUrl();
            const refreshed = await ConfigService.refresh();
            if (!refreshed) return;

            const currentUrl = ConfigService.getBackendUrl();
            if (currentUrl !== previousUrl) {
                syncApiBaseUrl();
                notification.info({
                    message: 'Backend Endpoint Updated',
                    description: `Server configuration changed. API requests now use ${currentUrl}.`,
                    placement: 'bottomRight',
                    duration: 6
                });
                console.log(`🔄 Backend URL changed: ${previousUrl} → ${currentUrl}`);
            } else {
                syncApiBaseUrl();
            }
        };

        const initializeApp = async () => {
            if (ConfigService.initializeFromCache()) {
                // Cached config available - render immediately, refresh silently
                syncApiBaseUrl();
                setIsInitialized(true);
                revalidateInBackground();
                return;
            }

            // First visit (no cache) - block on the server fetch
            try {
                console.log('🚀 Initializing application configuration...');
                await ConfigService.initialize();
                console.log('✅ Application configuration loaded successfully');
            } catch (error) {
                console.warn('⚠️ Configuration initialization failed, using fallback:', error);
                setInitError(error.message);
                // Still proceed with app - ConfigService has fallback handling
            }
            syncApiBaseUrl();
            setIsInitialized(true);
        };

        initializeApp();
    }, []);

    if (!isInitialized) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
                <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '40px',
                    textAlign: 'center',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                }}>
                    <Spin size="large" />
                    <div style={{
                        marginTop: '20px',
                        fontSize: '16px',
                        color: '#666',
                        fontWeight: '500'
                    }}>
                        Loading Configuration...
                    </div>
                    <div style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#999'
                    }}>
                        Connecting to backend services
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {initError && (
                <Alert
                    message="Configuration Warning"
                    description={`Failed to load server configuration: ${initError}. Using fallback settings.`}
                    type="warning"
                    closable
                    style={{
                        position: 'fixed',
                        top: 10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        maxWidth: '600px'
                    }}
                />
            )}
            {children}
        </>
    );
};

export default AppInitializer;
